import { useMemo, useState, useEffect } from 'react'
import { trackCompareUsed } from '@/lib/analytics-events'
import { Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Scale } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { EmptyState } from '@/components/shared/EmptyState'
import { ApiError } from '@/lib/api'
import { formatDate, cn } from '@/lib/utils'
import {
  useRegulatoryCompare,
  useRegulatoryFrameworks,
  type FrameworkRow,
  type RegulatoryCompareField,
  type RegulatoryCompareFrameworkSummary,
} from '@/hooks/useRegulatoryQueries'

const FIELD_ORDER: RegulatoryCompareField[] = [
  'scopeSummary',
  'applicability',
  'keyObligations',
  'penaltyStructure',
  'recentAmendments',
  'analystPublicNotes',
  'crosswalk',
  'citationLog',
]

const FIELD_LABELS: Record<RegulatoryCompareField, string> = {
  scopeSummary: 'Scope summary',
  applicability: 'Applicability',
  keyObligations: 'Key obligations',
  penaltyStructure: 'Penalty structure',
  recentAmendments: 'Recent amendments',
  analystPublicNotes: 'Analyst public notes',
  crosswalk: 'Crosswalk',
  citationLog: 'Citation log',
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

function isEmptyString(value: string): boolean {
  return value.trim().length === 0
}

function FieldValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-sm text-muted-foreground">—</span>
  }
  if (typeof value === 'string') {
    if (isEmptyString(value)) {
      return <span className="text-sm text-muted-foreground">—</span>
    }
    return <p className="whitespace-pre-wrap text-sm text-foreground">{value}</p>
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <p className="text-sm text-foreground">{String(value)}</p>
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-sm text-muted-foreground">—</span>
    }
    return (
      <ul className="flex flex-col gap-2">
        {value.map((el, idx) => (
          <li key={idx} className="text-sm text-foreground">
            {typeof el === 'string' ? (
              <span className="whitespace-pre-wrap">{el}</span>
            ) : el === null || el === undefined ? (
              <span className="text-muted-foreground">—</span>
            ) : typeof el === 'number' || typeof el === 'boolean' ? (
              String(el)
            ) : (
              <pre className="whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                {JSON.stringify(el, null, 2)}
              </pre>
            )}
          </li>
        ))}
      </ul>
    )
  }
  if (isPlainObject(value)) {
    if (Object.keys(value).length === 0) {
      return <span className="text-sm text-muted-foreground">—</span>
    }
    return (
      <pre className="whitespace-pre-wrap rounded bg-muted p-2 text-xs text-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    )
  }
  // Fallback for other primitives (bigint, symbol, etc.).
  return <p className="text-sm text-foreground">{String(value)}</p>
}

function FrameworkColumn({
  summary,
}: {
  summary: RegulatoryCompareFrameworkSummary
}) {
  return (
    <article className="flex flex-col gap-3 rounded-lg border bg-card p-4">
      <header className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-foreground">{summary.name}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-normal">
            {summary.jurisdiction}
          </Badge>
          <Badge variant="secondary" className="font-normal capitalize">
            {summary.coverageStatus}
          </Badge>
          {summary.updatedAt ? (
            <span className="text-xs text-muted-foreground">
              Updated {formatDate(summary.updatedAt)}
            </span>
          ) : null}
        </div>
      </header>
      <Link
        to={`/regulatory/frameworks/${summary.slug}`}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        Open framework detail
        <ExternalLink className="h-3 w-3" aria-hidden />
      </Link>
    </article>
  )
}

function CompareSkeleton() {
  return (
    <div
      className="flex flex-col gap-6"
      data-testid="regulatory-compare-skeleton"
    >
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

export function RegulatoryComparePage() {
  // KAN-32 regulatory:compare_used — once on mount.
  useEffect(() => {
    trackCompareUsed()
  }, [])

  const [frameworkAId, setFrameworkAId] = useState<string>('')
  const [frameworkBId, setFrameworkBId] = useState<string>('')

  const frameworksQuery = useRegulatoryFrameworks()
  const allFrameworks: FrameworkRow[] = useMemo(
    () => frameworksQuery.data?.frameworks ?? [],
    [frameworksQuery.data],
  )
  const fullFrameworks = useMemo(
    () => allFrameworks.filter((f) => f.coverageStatus === 'full'),
    [allFrameworks],
  )

  const sameSelection =
    Boolean(frameworkAId) &&
    Boolean(frameworkBId) &&
    frameworkAId === frameworkBId

  const compareQuery = useRegulatoryCompare(
    sameSelection ? undefined : frameworkAId || undefined,
    sameSelection ? undefined : frameworkBId || undefined,
  )

  const compareApiError =
    compareQuery.error instanceof ApiError ? compareQuery.error : null
  const compareIs422 = compareApiError?.status === 422

  const fields = compareQuery.data?.fields ?? []
  const orderedFields = useMemo(() => {
    const known = new Map<RegulatoryCompareField, (typeof fields)[number]>()
    for (const row of fields) known.set(row.field, row)
    const ordered: typeof fields = []
    for (const key of FIELD_ORDER) {
      const row = known.get(key)
      if (row) ordered.push(row)
    }
    // Keep any unknown future fields too — render them last with humanised label.
    for (const row of fields) {
      if (!FIELD_ORDER.includes(row.field)) ordered.push(row)
    }
    return ordered
  }, [fields])

  return (
    <main
      className="mx-auto flex max-w-6xl flex-col gap-6 p-6"
      aria-label="Regulatory compare"
      data-testid="regulatory-compare-page"
    >
      <nav aria-label="breadcrumb" className="text-xs text-muted-foreground">
        <Link
          to="/regulatory"
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          Regulatory radar
        </Link>
      </nav>

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Scale className="h-6 w-6" aria-hidden />
          Regulatory Compare
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Side-by-side comparison of two FULL coverage regulatory frameworks.
          Select two frameworks to view scope, obligations, penalties and
          analyst notes alongside each other.
        </p>
      </header>

      {frameworksQuery.isLoading ? (
        <Skeleton
          className="h-32 w-full"
          data-testid="regulatory-compare-frameworks-loading"
        />
      ) : frameworksQuery.isError ? (
        <ErrorState
          title="Could not load frameworks"
          message="The framework list is temporarily unavailable. Please try again."
          onRetry={() => frameworksQuery.refetch()}
        />
      ) : fullFrameworks.length < 2 ? (
        <EmptyState
          title="Not enough frameworks to compare"
          description="Two FULL coverage frameworks are required for a side-by-side comparison."
        />
      ) : (
        <>
          <section
            aria-label="Compare selectors"
            className="flex flex-col gap-4 rounded-lg border bg-card p-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FrameworkSelect
                id="compare-framework-a"
                label="Framework A"
                value={frameworkAId}
                allFrameworks={allFrameworks}
                onChange={setFrameworkAId}
              />
              <FrameworkSelect
                id="compare-framework-b"
                label="Framework B"
                value={frameworkBId}
                allFrameworks={allFrameworks}
                onChange={setFrameworkBId}
              />
            </div>
            {sameSelection ? (
              <p
                className="text-sm text-destructive"
                role="status"
                data-testid="compare-same-framework-warning"
              >
                Pick two different frameworks to compare.
              </p>
            ) : null}
          </section>

          {!frameworkAId || !frameworkBId ? (
            <p className="text-sm text-muted-foreground">
              Select two frameworks to begin.
            </p>
          ) : sameSelection ? null : compareQuery.isLoading ||
            compareQuery.isFetching ? (
            <CompareSkeleton />
          ) : compareIs422 ? (
            <ErrorState
              title="Comparison unavailable"
              message="One or both selected frameworks are not in FULL coverage. Please choose two FULL frameworks."
            />
          ) : compareQuery.isError ? (
            <ErrorState
              title="Could not load comparison"
              message="The comparison could not be loaded. Please try again."
              onRetry={() => compareQuery.refetch()}
            />
          ) : compareQuery.data ? (
            <>
              <section
                aria-label="Compared frameworks"
                className="grid gap-6 lg:grid-cols-2"
              >
                <FrameworkColumn summary={compareQuery.data.frameworks.a} />
                <FrameworkColumn summary={compareQuery.data.frameworks.b} />
              </section>

              <section
                aria-label="Side-by-side fields"
                className="flex flex-col gap-4"
              >
                <h2 className="text-lg font-semibold">Field comparison</h2>
                <div className="flex flex-col divide-y rounded-lg border bg-card">
                  {orderedFields.map((row) => (
                    <div
                      key={row.field}
                      className={cn(
                        'grid gap-4 p-4 lg:grid-cols-[12rem_1fr_1fr]',
                      )}
                      data-testid={`compare-field-${row.field}`}
                    >
                      <div className="text-sm font-medium text-foreground">
                        {FIELD_LABELS[row.field] ?? humaniseField(row.field)}
                      </div>
                      <div className="min-w-0">
                        <FieldValue value={row.a} />
                      </div>
                      <div className="min-w-0">
                        <FieldValue value={row.b} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : null}
        </>
      )}
    </main>
  )
}

function FrameworkSelect({
  id,
  label,
  value,
  allFrameworks,
  onChange,
}: {
  id: string
  label: string
  value: string
  allFrameworks: FrameworkRow[]
  onChange: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 min-w-[10rem] rounded-md border bg-background px-3 text-sm"
      >
        <option value="">Choose a framework</option>
        {allFrameworks.map((f) => (
          <option
            key={f.id}
            value={f.id}
            disabled={f.coverageStatus !== 'full'}
          >
            {f.name}
            {f.coverageStatus !== 'full' ? ` (${f.coverageStatus})` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

function humaniseField(field: string): string {
  // Fallback for any future field values not in FIELD_LABELS.
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}
